import { Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IEmployment } from '@common/interfaces';
import type { Id } from '@common/utils/id';
import type { IOption, IPractitioner } from '@frontend/components/interfaces';
import { getDisplayName } from '@common/utils/getDisplayName';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '../../utils/getValidationErrorTranslation';

interface KsyosPractitionersDropdownProps {
    className?: string;
    currentPractitionerId: Id | null;
    errorKey?: string;
    onChange: (option: IOption<string> | null) => void;
}

export function KsyosPractitionersDropdown(props: Readonly<KsyosPractitionersDropdownProps>) {
    const [employments, setEmployments] = useState<(IEmployment & { practitioner: IPractitioner })[]>();
    const [selectedPractitioner, setSelectedPractitioner] = useState<IOption<string> | null>();

    const translate = getSelectedLanguage();

    useEffect(() => {
        loadEmployments();
    }, []);

    useEffect(() => {
        if (props.currentPractitionerId) {
            const currentEmployment = employments?.find(
                (employment) => employment.practitioner.id === props.currentPractitionerId
            );

            if (currentEmployment) {
                setSelectedPractitioner({
                    label: getPractitionerName(currentEmployment?.practitioner),
                    value: props.currentPractitionerId,
                });
            }
        } else if (selectedPractitioner) {
            setSelectedPractitioner(null);
        }
    }, [employments, props.currentPractitionerId]);

    return (
        <Select
            className={props.className}
            error={getValidationErrorTranslation(props.errorKey)}
            onChange={(option) => {
                props.onChange(option);
                setSelectedPractitioner(option);
            }}
            options={getOptions()}
            placeholder={translate.common.selectOne}
            value={selectedPractitioner}
        />
    );

    function getPractitionerName(practitioner: IPractitioner) {
        return getDisplayName(practitioner, false, 'nameGivenNameFamily');
    }

    async function loadEmployments() {
        const result = await fetchJson('/api/ksyos/employments?isActive=true', 'GET');
        setEmployments(result.json.items);
    }

    function getOptions() {
        return employments?.map((employment) => ({
            label: getPractitionerName(employment.practitioner),
            value: employment.practitioner.id,
        }));
    }
}
